// =============================================================
//  EditProfileScreenAdmin — Modifier son profil + mot de passe
// =============================================================

import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, Alert } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import {
  getAuth,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  deleteUser,
} from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';

import AdminLayout from './web-ui/AdminLayout';
import { Card, Button, Input, Divider, Avatar, Loading } from './web-ui/kit';
import { colors, spacing } from './web-ui/theme';

export default function EditProfileScreenAdmin() {
  const navigation = useNavigation();
  const auth = getAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ fullName: '', email: '', phone: '', city: '' });
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [errors, setErrors] = useState({});
  const [pwErrors, setPwErrors] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        const snap = await getDoc(doc(db, 'admin', user.uid));
        const data = snap.exists() ? snap.data() : {};
        setForm({
          fullName: data.fullName || '',
          email: user.email || '',
          phone: data.phone?.replace(/^\+237/, '') || '',
          city: data.city || '',
        });
      } catch (e) {
        console.log('load profile error', e?.message || e);
      } finally {
        setLoading(false);
      }
    })();
  }, [auth]);

  const validateProfile = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = 'Le nom est requis';
    if (form.phone && !/^\d{9}$/.test(form.phone.trim())) e.phone = 'Format invalide (9 chiffres)';
    return e;
  };

  const validatePw = () => {
    const e = {};
    if (!pw.current) e.current = 'Mot de passe actuel requis';
    if (pw.next.length < 6) e.next = 'Minimum 6 caractères';
    if (pw.next !== pw.confirm) e.confirm = 'Les mots de passe ne correspondent pas';
    return e;
  };

  const saveProfile = async () => {
    const e = validateProfile();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      const user = auth.currentUser;
      await updateDoc(doc(db, 'admin', user.uid), {
        fullName: form.fullName.trim(),
        phone: form.phone ? `+237${form.phone.trim()}` : '',
        city: form.city.trim(),
        updatedAt: new Date(),
      });
      Alert.alert('Succès', 'Profil mis à jour');
    } catch (err) {
      console.log('save profile error', err?.message || err);
      Alert.alert('Erreur', 'Mise à jour impossible');
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    const e = validatePw();
    if (Object.keys(e).length) {
      setPwErrors(e);
      return;
    }
    setPwErrors({});
    setSaving(true);
    try {
      const user = auth.currentUser;
      const cred = EmailAuthProvider.credential(user.email, pw.current);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, pw.next);
      Alert.alert('Succès', 'Mot de passe modifié');
      setPw({ current: '', next: '', confirm: '' });
    } catch (err) {
      Alert.alert('Erreur', 'Mot de passe actuel incorrect');
    } finally {
      setSaving(false);
    }
  };

  const removeAccount = async () => {
    if (!confirm('Supprimer définitivement votre compte ? Cette action est irréversible.')) return;
    try {
      const user = auth.currentUser;
      await deleteUser(user);
    } catch (err) {
      Alert.alert('Erreur', 'Reconnectez-vous puis réessayez');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Mon profil">
        <Loading label="Chargement..." />
      </AdminLayout>
    );
  }

  const initials = (form.fullName || form.email || 'A').slice(0, 2).toUpperCase();

  return (
    <AdminLayout title="Mon profil" subtitle="Informations personnelles et sécurité">
      <View style={styles.actions}>
        <Button variant="secondary" icon="arrow-left" onPress={() => navigation.goBack()}>Retour</Button>
      </View>

      <View style={styles.row}>
        <Card style={{ flex: 2, minWidth: 320, gap: spacing.md }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.md }}>
            <Avatar name={initials} size={64} />
            <View>
              <Text style={styles.name}>{form.fullName || '—'}</Text>
              <Text style={styles.email}>{form.email}</Text>
            </View>
          </View>

          <Divider />

          <Text style={styles.section}>Informations</Text>
          <Field label="Nom complet" error={errors.fullName}>
            <Input icon="account-outline" value={form.fullName} onChangeText={(t) => setForm((s) => ({ ...s, fullName: t }))} />
          </Field>
          <Field label="Email">
            <Input icon="email-outline" value={form.email} editable={false} />
          </Field>
          <Field label="Téléphone" error={errors.phone}>
            <Input icon="phone-outline" value={form.phone} onChangeText={(t) => setForm((s) => ({ ...s, phone: t }))} type="number" placeholder="6XXXXXXXX" />
          </Field>
          <Field label="Ville">
            <Input icon="map-marker-outline" value={form.city} onChangeText={(t) => setForm((s) => ({ ...s, city: t }))} />
          </Field>

          <Button icon="content-save-outline" loading={saving} onPress={saveProfile}>
            Enregistrer
          </Button>
        </Card>

        <Card style={{ flex: 1, minWidth: 280, gap: spacing.md, height: 'fit-content' }}>
          <Text style={styles.section}>Changer le mot de passe</Text>
          <Field label="Mot de passe actuel" error={pwErrors.current}>
            <Input
              icon="lock-outline"
              value={pw.current}
              onChangeText={(t) => setPw((s) => ({ ...s, current: t }))}
              type={show.current ? 'text' : 'password'}
              rightIcon={show.current ? 'eye-off-outline' : 'eye-outline'}
              onRightIconPress={() => setShow((s) => ({ ...s, current: !s.current }))}
            />
          </Field>
          <Field label="Nouveau mot de passe" error={pwErrors.next}>
            <Input
              icon="lock-plus-outline"
              value={pw.next}
              onChangeText={(t) => setPw((s) => ({ ...s, next: t }))}
              type={show.next ? 'text' : 'password'}
              rightIcon={show.next ? 'eye-off-outline' : 'eye-outline'}
              onRightIconPress={() => setShow((s) => ({ ...s, next: !s.next }))}
            />
          </Field>
          <Field label="Confirmer" error={pwErrors.confirm}>
            <Input
              icon="lock-check-outline"
              value={pw.confirm}
              onChangeText={(t) => setPw((s) => ({ ...s, confirm: t }))}
              type={show.confirm ? 'text' : 'password'}
              rightIcon={show.confirm ? 'eye-off-outline' : 'eye-outline'}
              onRightIconPress={() => setShow((s) => ({ ...s, confirm: !s.confirm }))}
            />
          </Field>
          <Button icon="key-outline" loading={saving} onPress={changePassword}>
            Modifier le mot de passe
          </Button>

          <Divider />

          <Button variant="danger" icon="account-remove-outline" onPress={removeAccount}>
            Supprimer mon compte
          </Button>
        </Card>
      </View>
    </AdminLayout>
  );
}

function Field({ label, error, children }) {
  return (
    <View style={{ gap: 6 }}>
      <Text style={styles.label}>{label}</Text>
      {children}
      {!!error && <Text style={styles.error}>{error}</Text>}
    </View>
  );
}

const styles = StyleSheet.create({
  actions: { flexDirection: 'row', marginBottom: spacing.md },
  row: { flexDirection: 'row', gap: spacing.lg, flexWrap: 'wrap', alignItems: 'flex-start' },
  name: { fontSize: 18, fontWeight: '700', color: colors.text },
  email: { fontSize: 13, color: colors.textSecondary, marginTop: 2 },
  section: { fontSize: 14, fontWeight: '700', color: colors.text },
  label: { fontSize: 12, fontWeight: '600', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.3 },
  error: { fontSize: 12, color: colors.danger, marginTop: 2 },
});
